<template>
  <v-container>
    <v-row v-if="loadingRecords" justify="center" align="center">
      <v-progress-circular indeterminate :size="60" color="primary" />
    </v-row>

    <v-row v-else-if="APIError" justify="center" align="center">
      <p>
        <v-icon color="error">mdi-alert-circle-outline</v-icon> Sorry, something
        went wrong. Please try again later.
      </p>
    </v-row>

    <v-container v-else>
      <v-row>
        <TrainingWeightCard
          title="Past 7 Days"
          :weight="lastWeekWeightChange"
        ></TrainingWeightCard>
        <TrainingWeightCard
          title="Past 30 Days"
          :weight="lastMonthWeightChange"
        ></TrainingWeightCard>
        <TrainingWeightCard
          title="Average per Week"
          :weight="averageWeeklyWeightChange"
        ></TrainingWeightCard>
        <TrainingWeightCard
          title="Average per Month"
          :weight="averageMonthlyWeightChange"
        ></TrainingWeightCard>
      </v-row>
      <v-row>
        <v-col cols="12" lg="6">
          <v-card>
            <v-card-title>
              <p class="headline">Weekly Average Weight</p>
            </v-card-title>
            <apexchart
              height="300"
              :options="weeklyAverageChartOptions"
              :series="[weeklyAverageChartSeries]"
            ></apexchart>
          </v-card>
        </v-col>
        <v-col cols="12" lg="6">
          <v-card>
            <v-card-title>
              <p class="headline">Weight Records</p>
            </v-card-title>
            <apexchart
              height="300"
              :options="chartOptions"
              :series="[weightChartSeries]"
            ></apexchart>
          </v-card>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Weight</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="weightRecord in weightRecordsList"
                      :key="weightRecord.id"
                    >
                      <td>{{ weightRecord.date }}</td>
                      <td>{{ weightRecord.weight }}</td>
                      <td>
                        <v-icon class="mr-4" @click="editRecord(weightRecord)">
                          mdi-pencil
                        </v-icon>
                        <v-icon @click="deleteRecord(weightRecord)">
                          mdi-delete
                        </v-icon>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
            <v-card-actions
              v-if="weightRecordsListAmount <= weightRecordsList.length"
              class="justify-center"
              @click="updateWeightRecordsListAmount"
            >
              <v-btn class="mb-2" small>Load more</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-btn bottom color="primary" dark fab fixed right @click="createNewRecord">
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <v-dialog v-model="dialog" width="800px">
      <TrainingWeightForm
        :key="dialogKey"
        :propsRecord="selectedRecord"
        :recordedDates="recordedDates"
        :action="dialogAction"
        @close-dialog="closeDialog"
      >
      </TrainingWeightForm>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import moment from "moment";
import VueApexCharts from 'vue-apexcharts'
import { mapState } from 'vuex'
import TrainingWeightCard from "./TrainingWeightCard"
import TrainingWeightForm from "./TrainingWeightForm"

export default {
  name: "TrainingWeight",

  components: {
    apexchart: VueApexCharts,
    TrainingWeightCard,
    TrainingWeightForm
  },

  data: () => ({
    APIError: false,
    loadingRecords: false,
    dialog: false,
    dialogAction: "create",
    dialogKey: "",
    selectedRecord: undefined,
    weightRecordsListAmount: 14,
    chartOptions: {
      chart: {
        fontFamily: 'Roboto, sans-serif',
        animations: {
          enabled: false,
        },
        toolbar: {
          show: false
        }
      },
      markers: {
        size: 3,
      },
      xaxis: {
        type: 'datetime',
      }
    }
  }),

  mounted() {
    if (this.weightRecords.length == 0) {
      this.getData()
    }
  },

  computed: {
    ...mapState([
      'weightRecords',
    ]),
    weeklyAverageChartOptions: function () {
      return {
        ...this.chartOptions,
        dataLabels: {
          enabled: true,
          offsetY: -22
        },
        xaxis: {
          type: 'category',
          labels: {
            formatter: function (val) {
              return moment(val).format("DD MMM");
            }
          }
        }
      }
    },
    weightRecordsList: function () {
      return this.weightRecords.slice(0, this.weightRecordsListAmount)
    },
    weightChartSeries: function () {
      const chartData = this.weightRecords.map(weightRecord => {
        return {
          x: weightRecord.date,
          y: weightRecord.weight
        }
      })
      const series = {
        type: "line",
        name: "Weight",
        data: chartData
      }
      return series
    },
    weightRecordsGroupedByWeek: function () {
      let groupedRecords = {}
      this.weightRecords.forEach(weightRecord => {
        const weekId = moment(weightRecord.date).endOf('isoWeek').format('YYYY-MM-DD')

        if (typeof groupedRecords[weekId] === 'undefined') {
          groupedRecords[weekId] = [];
        }

        groupedRecords[weekId].push(parseFloat(weightRecord.weight));
      })
      return groupedRecords
    },
    weeklyAverageChartData: function () {
      return Object.keys(this.weightRecordsGroupedByWeek).map((date) => {
        const weeklyWeightRecords = this.weightRecordsGroupedByWeek[date]
        const weeklyWeightRecordsSum = weeklyWeightRecords.reduce((a, b) => (a + b))
        const weeklyAverageWeight = weeklyWeightRecordsSum / weeklyWeightRecords.length
        return {
          x: date,
          y: weeklyAverageWeight.toFixed(1),
        }
      })
    },
    weeklyAverageChartSeries: function () {
      const chartData = [...this.weeklyAverageChartData].reverse()
      const series = {
        type: "bar",
        name: "Weekly Average",
        data: chartData
      }
      return series
    },
    previousWeekAverageWeight: function () {
      return this.getAverageWeightByDate(this.getPastDateByNumberOfWeeks(1))
    },
    lastWeekWeightChange: function () {
      const nextToLastWeekAverageWeight = this.getAverageWeightByDate(
        this.getPastDateByNumberOfWeeks(2)
      )
      const result = this.previousWeekAverageWeight - nextToLastWeekAverageWeight
      return nextToLastWeekAverageWeight ? result : 0
    },
    lastMonthWeightChange: function () {
      const LastMonthAverageWeight = this.getAverageWeightByDate(
        this.getPastDateByNumberOfWeeks(4)
      )
      const result = this.previousWeekAverageWeight - LastMonthAverageWeight
      return LastMonthAverageWeight ? result : 0
    },
    averageDailyWeightChange: function () {
      if (this.weightRecords.length === 0) {
        return 0
      }
      const firstRecord = this.weightRecords[this.weightRecords.length - 1]
      const lastRecord = this.weightRecords[0]
      const startDate = moment(firstRecord.date)
      const endDate = moment(lastRecord.date)
      const numberOfDays = endDate.diff(startDate, 'days')
      const startWeight = firstRecord.weight
      const endWeight = lastRecord.weight
      const weightDifference = endWeight - startWeight
      return weightDifference / numberOfDays
    },
    averageWeeklyWeightChange: function () {
      return this.averageDailyWeightChange * 7
    },
    averageMonthlyWeightChange: function () {
      return this.averageDailyWeightChange * 30
    },
    recordedDates: function () {
      return this.weightRecords.map(record => record.date)
    },
  },

  methods: {
    getData: function () {
      this.loadingRecords = true
      this.APIError = false
      axios
        .get(
          `${process.env.VUE_APP_API_ROOT_URL}training/weight/`,
          this.$store.getters.axiosConfig
        )
        .then(response => {
          this.$store.commit("updateWeightRecords", response.data)
          this.loadingRecords = false
        })
        .catch(() => {
          this.APIError = true
          this.loadingRecords = false
        })
    },
    updateWeightRecordsListAmount: function () {
      this.weightRecordsListAmount += 7
    },
    getPastDateByNumberOfWeeks: function (numberOfWeeks) {
      return moment().subtract(numberOfWeeks, 'weeks').endOf('isoWeek').format("YYYY-MM-DD")
    },
    getAverageWeightByDate: function (date) {
      const data = this.weeklyAverageChartData.find(data => {
        return data.x === date
      })
      if (typeof data === "undefined") {
        return null
      }
      return data.y
    },
    createNewRecord: function () {
      this.selectedRecord = undefined
      this.dialogAction = "create"
      this.openDialog()
    },
    editRecord: function (record) {
      this.selectedRecord = record
      this.dialogAction = "edit"
      this.openDialog()
    },
    deleteRecord: function (record) {
      this.selectedRecord = record
      this.dialogAction = "delete"
      this.openDialog()
    },
    openDialog: function () {
      this.dialogKey = this.selectedRecord ? this.selectedRecord.id : "new"
      this.dialog = true
    },
    closeDialog: function () {
      this.dialogKey = ""
      this.dialog = false
    },
  }
}
</script>